import { useRef, useState } from 'react';
import useOnClickOutside from 'react-cool-onclickoutside';
import cn from 'classnames';

import type { RoomWithPeople } from '@/interfaces';
import { InputErrors } from '@/components/inputErrors/InputErrors';

import { HotelRoomList } from './components/hotelRoomList/HotelRoomList';
import styles from './TravellersPicker.module.css';

export interface TravellersPickerProps {
    rooms: readonly RoomWithPeople[];
    updateRooms: (rooms: readonly RoomWithPeople[]) => void;
    errors?: readonly string[];
}

export const generateRoomInfoText = (rooms: readonly RoomWithPeople[]): string => {
    const numberOfAdults = rooms.reduce((sum: number, room: RoomWithPeople): number => sum + room.adults, 0);
    const numberOfChildren = rooms.reduce((sum: number, room: RoomWithPeople): number => sum + room.children.length, 0);
    const roomsText = `${rooms.length} rom`;
    const adultsText = numberOfAdults !== 1 ? `${numberOfAdults} voksne` : `${numberOfAdults} voksen`;

    if (numberOfChildren > 0) {
        const childrenText = `${numberOfChildren} barn`;
        return `${adultsText}, ${childrenText} i ${roomsText}`;
    }
    return `${adultsText} i ${roomsText}`;
};

export const TravellersPicker = ({ rooms, updateRooms, errors = [] }: TravellersPickerProps) => {
    const [openDropdown, setOpenDropdown] = useState(false);
    const ref = useRef();

    useOnClickOutside(
        (): void => {
            setOpenDropdown(false);
        },
        {
            refs: [ref],
            disabled: !openDropdown,
        },
    );

    return (
        <div className={styles.travellersPicker} data-testid="TravellersPicker">
            <div
                id="traveller-container"
                className={cn('u-display-inline-block u-size1of1 input input--select__wrap dropdown-container u-valign-top', {
                    'input--is-invalid': errors.length > 0,
                })}
                ref={ref}>
                <label htmlFor="hotelRoomDropdown">
                    Personer
                    <span className="u-screen-reader-only">{generateRoomInfoText(rooms)}</span>
                </label>
                <div aria-live="polite" className="u-screen-reader-only">
                    {rooms.length} rom
                </div>
                <button
                    type="button"
                    id="hotelRoomDropdown"
                    onClick={() => setOpenDropdown(!openDropdown)}
                    className="transparent u-size1of1 u-overflow-hidden"
                    data-testid="hotelRoomDropdown">
                    <div className="dropdown-display clicked">
                        <span data-testid="hotel-room-text" className="u-pl8 no-text-select">
                            {generateRoomInfoText(rooms)}
                        </span>
                    </div>
                </button>
                {openDropdown ? (
                    <HotelRoomList
                        rooms={rooms}
                        updateRooms={updateRooms}
                        roomLimits={[1, 5]}
                        adultLimits={[1, 9]}
                        childLimits={[0, 4]}
                        onDismiss={() => setOpenDropdown(false)}
                    />
                ) : null}
                <InputErrors errors={errors} />
            </div>
        </div>
    );
};
